import { Grid, List, ListItemButton, ListItemText, Paper, Typography } from '@mui/material';
import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router';

import { SearchTextField } from 'app/components/form-controls/search-text-field';
import { GeneralConstants } from 'app/constants/general-constants';
import { Brand } from 'app/model/cris/brand';
import { Category } from 'app/model/cris/category';
import { Store } from 'app/model/cris/store';
import { ListOptions } from 'app/model/list-options';
import { brandService } from 'app/services/brand.service';
import { categoryService } from 'app/services/category.service';
import { storeService } from 'app/services/store.service';

import { selectProdDataStatus } from './cris.slice';

type TaxonomyProps = RouteComponentProps;

const Taxonomy: React.FC<TaxonomyProps> = ({ history }) => {
    const dispatch = useDispatch();
    const showProd = useSelector(selectProdDataStatus);

    const [categories, setCategories] = useState<Category[]>([]);
    const [brands, setBrands] = useState<Brand[]>([]);
    const [stores, setStores] = useState<Store[]>([]);
    const [categoriesSearch, setCategoriesSearch] = useState('');
    const [brandsSearch, setBrandsSearch] = useState('');
    const [storesSearch, setStoresSearch] = useState('');
    const [selectedCatId, setSelectedCatId] = useState<string | undefined>('');
    const [selectedBrandId, setSelectedBrandId] = useState<string | undefined>('');
    const [selectedStoreId] = useState<number | undefined>();

    const handleCategorySearch = (value: string) => {
        setCategoriesSearch(value);
    };

    const handleBrandSearch = (value: string) => {
        setBrandsSearch(value);
    };

    const handleStoreSearch = (value: string) => {
        setStoresSearch(value);
    };

    useEffect(() => {
        const loadCategoriesData = () => {
            categoryService
                .listAll({ pageSize: GeneralConstants.PAGE_SIZE_HUGE })
                .subscribe((categoryData) => setCategories(categoryData.filter((cat) => 0 !== Number(cat.id))));
        };

        loadCategoriesData();
        setBrands([]);
        setStores([]);
    }, [dispatch, showProd]);

    const loadBrands = useCallback(
        (catId: string) => {
            const opts = catId && '' !== catId ? { filters: { category: catId } } : {};
            brandService.listAll({ ...opts, pageSize: GeneralConstants.PAGE_SIZE_HUGE }).subscribe((brandData) => {
                setBrands(brandData);
            });
        },
        [dispatch],
    );

    const loadStores = useCallback(
        (categoryId?: string, brandId?: string) => {
            if (!categoryId && !brandId) {
                setStores([]);

                return;
            }

            const opts: ListOptions = { pageSize: GeneralConstants.PAGE_SIZE_HUGE, queryFilters: {} };
            if (categoryId) {
                opts.queryFilters = { ...opts.queryFilters, category: categoryId };
            }
            if (brandId) {
                opts.queryFilters = { ...opts.queryFilters, brand: brandId };
            }

            storeService.listAll(opts).subscribe((storeData) => setStores(storeData));
        },
        [dispatch],
    );

    const selectCategory = (id: string) => {
        setSelectedCatId(id);
        loadBrands(id);
        loadStores(id, selectedBrandId);
    };

    const selectBrand = (id: string) => {
        setSelectedBrandId(id);
        loadStores(selectedCatId, id);
    };

    return (
        <Grid container spacing={1}>
            <Grid item xs={12}>
                <Typography variant='body1' className='mb-1'>
                    Double click on any entity to see more detailed view. Select category or brand to list stores.
                </Typography>
            </Grid>
            <Grid item xs={4}>
                <Paper elevation={3} style={{ height: '30em', overflow: 'auto' }}>
                    <Typography variant='caption'>Categories</Typography>
                    <SearchTextField value={categoriesSearch} onSearch={handleCategorySearch} />
                    <List dense>
                        {/* Render filtered categories */}
                        <ListItemButton selected={'' === selectedCatId} onClick={() => selectCategory('')}>
                            <ListItemText primary='(Any Category)' />
                        </ListItemButton>
                        {categories
                            .filter((category) => {
                                const searchRegex = new RegExp(categoriesSearch, 'i');

                                return searchRegex.test(category.category ?? '');
                            })
                            .map((category) => (
                                <ListItemButton
                                    selected={category.id === selectedCatId}
                                    key={category.id}
                                    onClick={() => selectCategory(category.id)}
                                >
                                    <ListItemText primary={category.category} />
                                </ListItemButton>
                            ))}
                    </List>
                </Paper>
            </Grid>

            <Grid item xs={4}>
                <Paper elevation={3} style={{ height: '30em', overflow: 'auto' }}>
                    <Typography variant='caption'>Brands</Typography>
                    <SearchTextField value={brandsSearch} onSearch={handleBrandSearch} />
                    <List dense>
                        {/* Render filtered brands */}
                        {brands
                            .filter((brand) => {
                                const searchRegex = new RegExp(brandsSearch, 'i');

                                return searchRegex.test(brand.name);
                            })
                            .map((brand) => (
                                <ListItemButton
                                    selected={brand.id === selectedBrandId}
                                    key={brand.id}
                                    onClick={() => selectBrand(brand.id)}
                                    onDoubleClick={() => history.push(`/cris/brands/${brand.id}/details`)}
                                >
                                    <ListItemText primary={brand.name} />
                                </ListItemButton>
                            ))}
                    </List>
                </Paper>
            </Grid>

            <Grid item xs={4}>
                <Paper elevation={3} style={{ height: '30em', overflow: 'auto' }}>
                    <Typography variant='caption'>Stores</Typography>
                    <SearchTextField value={storesSearch} onSearch={handleStoreSearch} />
                    <List dense>
                        {/* Render filtered stores */}
                        {stores
                            .filter((store) => {
                                const searchRegex = new RegExp(storesSearch, 'i');

                                return searchRegex.test(store.name);
                            })
                            .map((store) => (
                                <ListItemButton
                                    selected={store.id === selectedStoreId}
                                    key={store.id}
                                    onDoubleClick={() => history.push(`/cris/stores/${store.id}`)}
                                >
                                    <ListItemText primary={store.name} />
                                </ListItemButton>
                            ))}
                    </List>
                </Paper>
            </Grid>
        </Grid>
    );
};

const enhanced = withRouter(Taxonomy);

export { enhanced as Taxonomy };
